import { FilterQueryParams, FilterState, getDefaultFilterState } from 'Modules/types/filterTypes'

/**
 * Parses URL query parameters and updates the filterState accordingly.
 * @param query - The URL query parameters.
 * @param filterState - The reactive filter state to update.
 */
export function parseQueryToFilterState(query: Record<string, any>, filterState: FilterState): void {
  // Iterate over filterState properties and update based on query
  Object.entries(filterState).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      const defaultValue = getDefaultFilterState()[key as keyof FilterState]
      const hasDefaultItems = Array.isArray(defaultValue) && defaultValue.length > 0
      
      if (hasDefaultItems) {
        if (!query[key]) {
          filterState[key as keyof FilterState] = defaultValue as any
        } else if (query[key] === 'none' || (Array.isArray(query[key]) && query[key].includes('none'))) {
          filterState[key as keyof FilterState] = [] as any
        } else {
          filterState[key as keyof FilterState] = Array.isArray(query[key]) ? query[key] : [query[key]] as any
        }
      } else {
        // If the filterState value is an array, update it from the query
        filterState[key as keyof FilterState] = query[key] ? (Array.isArray(query[key]) ? query[key] : [query[key]]) : [] as any
      }
    }
  })

  // Handle sizes separately (since it's a nested object)
  filterState.sizes.area.min = query.areaMin ? Number(query.areaMin) : null
  filterState.sizes.area.max = query.areaMax ? Number(query.areaMax) : null
  filterState.sizes.length.min = query.lengthMin ? Number(query.lengthMin) : null
  filterState.sizes.length.max = query.lengthMax ? Number(query.lengthMax) : null
}

/**
 * Builds URL query parameters from the filterState.
 * @param filterState - The current filter state.
 * @returns An object representing the URL query parameters.
 */
export function buildQueryObjectFromFilterState(filterState: FilterState): FilterQueryParams {
  const query: FilterQueryParams = {}
  Object.entries(filterState).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      const defaultValue = getDefaultFilterState()[key as keyof FilterState]
      const hasDefaultItems = Array.isArray(defaultValue) && defaultValue.length > 0
      
      if (hasDefaultItems) {
        if (value.length === defaultValue.length) {
          // All selected, omit from query
        } else if (value.length === 0) {
          query[key as keyof FilterQueryParams] = ['none'] as any
        } else {
          query[key as keyof FilterQueryParams] = value.map(String) as any
        }
      } else if (value.length > 0) {
        query[key as keyof FilterQueryParams] = value.map(String) as any // Convert array values to strings
      }
    }
  })

  // Sizes
  if (filterState.sizes.area.min !== null) {
    query.areaMin = filterState.sizes.area.min.toString()
  }
  if (filterState.sizes.area.max !== null) {
    query.areaMax = filterState.sizes.area.max.toString()
  }
  if (filterState.sizes.length.min !== null) {
    query.lengthMin = filterState.sizes.length.min.toString()
  }
  if (filterState.sizes.length.max !== null) {
    query.lengthMax = filterState.sizes.length.max.toString()
  }

  return query
}
